import classNames from "classnames";
import { Immutable } from "immer";
import React, { useEffect, useState } from "react";
import SVG from "react-inlinesvg";

import { getWindow } from "@thelabnyc/thelabui/src/utils/dom";

// Icons
import iconBackSleepers from "../../../../img/icons/mattress-match/back-sleepers.svg";
import iconBudgetBalance from "../../../../img/icons/mattress-match/budget-balance.svg";
import iconBudgetForever from "../../../../img/icons/mattress-match/budget-forever.svg";
import iconBudgetPiggy from "../../../../img/icons/mattress-match/budget-piggy.svg";
import iconCoolingHot from "../../../../img/icons/mattress-match/cooling-hot.svg";
import iconCoolingMild from "../../../../img/icons/mattress-match/cooling-mild.svg";
import iconCoolingNoHeat from "../../../../img/icons/mattress-match/cooling-no-heat.svg";
import iconCouple from "../../../../img/icons/mattress-match/couple.svg";
import iconExit from "../../../../img/icons/mattress-match/exit.svg";
import iconFirm from "../../../../img/icons/mattress-match/firm.svg";
import iconHeavy from "../../../../img/icons/mattress-match/heavy.svg";
import iconMiddle from "../../../../img/icons/mattress-match/height-middle.svg";
import iconHybrid from "../../../../img/icons/mattress-match/hybrid.svg";
import iconLight from "../../../../img/icons/mattress-match/light.svg";
import iconLightbulb from "../../../../img/icons/mattress-match/lightbulb.svg";
import iconMediumHybrid from "../../../../img/icons/mattress-match/medium-hybrid.svg";
import iconMedium from "../../../../img/icons/mattress-match/medium.svg";
import iconMild from "../../../../img/icons/mattress-match/mild.svg";
import iconNoDisturb from "../../../../img/icons/mattress-match/no-disturb.svg";
import iconNo from "../../../../img/icons/mattress-match/no.svg";
import iconNumberOne from "../../../../img/icons/mattress-match/number-one.svg";
import iconNumberTwo from "../../../../img/icons/mattress-match/number-two.svg";
import iconPartner from "../../../../img/icons/mattress-match/partner.svg";
import iconPiggy from "../../../../img/icons/mattress-match/piggy.svg";
import iconShort from "../../../../img/icons/mattress-match/short.svg";
import iconSideSleepers from "../../../../img/icons/mattress-match/side-sleepers.svg";
import iconSleepProfile from "../../../../img/icons/mattress-match/sleep-profile.svg";
import iconSnore from "../../../../img/icons/mattress-match/snore.svg";
import iconSoft from "../../../../img/icons/mattress-match/soft.svg";
import iconStomachSleepers from "../../../../img/icons/mattress-match/stomach-sleepers.svg";
import iconTall from "../../../../img/icons/mattress-match/tall.svg";
import iconWeight from "../../../../img/icons/mattress-match/weight.svg";
import { Link } from "../../../components/Link";
import { RichText } from "../../../components/RichText";
import { FormCheckbox } from "../../../forms";
import { strings } from "../../../localization";
import { usePageURL } from "../../../utils/urls";
import { SVGLoader } from "../SVGLoader";
import { Step } from "../constants";
import { useStepInformation } from "../hooks/useStepInformation";
import { OptionStepsType, StepDataType } from "../models";

import styles from "./index.module.scss";

const icons = {
    "back": iconBackSleepers,
    "budget-balance": iconBudgetBalance,
    "budget-forever": iconBudgetForever,
    "budget-piggy": iconBudgetPiggy,
    "cooling-hot": iconCoolingHot,
    "cooling-mild": iconCoolingMild,
    "cooling-no-heat": iconCoolingNoHeat,
    "couple": iconCouple,
    "exit": iconExit,
    "firm": iconFirm,
    "height-middle": iconMiddle,
    "hybrid": iconHybrid,
    "lightbulb": iconLightbulb,
    "medium": iconMedium,
    "medium-hybrid": iconMediumHybrid,
    "mild": iconMild,
    "no": iconNo,
    "no-disturb": iconNoDisturb,
    "number-one": iconNumberOne,
    "number-two": iconNumberTwo,
    "partner": iconPartner,
    "piggy": iconPiggy,
    "height-short": iconShort,
    "side": iconSideSleepers,
    "sleep-profile": iconSleepProfile,
    "snore": iconSnore,
    "soft": iconSoft,
    "stomach": iconStomachSleepers,
    "height-tall": iconTall,
    "weight-light": iconLight,
    "weight-middle": iconWeight,
    "weight-heavy": iconHeavy,
} as const;

const LARGE_WIDTH_THRESHOLD = 800;

type IProps = Immutable<{
    step: keyof OptionStepsType;
}>;

export const MattressOption = (props: IProps): React.JSX.Element | null => {
    const window = getWindow();
    const [isLargeScreen, setIsLargeScreen] = useState<boolean>(
        window ? window.innerWidth > LARGE_WIDTH_THRESHOLD : false,
    );
    const financeLink = usePageURL("finance_link");
    const homeLink = usePageURL("home");
    useEffect(() => {
        const window = getWindow();
        if (!window) return;

        const handleResize = () => {
            if (window) {
                setIsLargeScreen(window.innerWidth > LARGE_WIDTH_THRESHOLD);
            }
        };
        window.addEventListener("resize", handleResize);
        return () => {
            if (window) {
                window.removeEventListener("resize", handleResize);
            }
        };
    }, []);
    const info = useStepInformation({
        step: props.step,
    });
    if (!info) {
        return null;
    }
    const { stepInfo, selectedLevel, onClick } = info;
    if (!stepInfo) {
        return null;
    }
    const renderBudgetTip = () => {
        return (
            <section className={styles.optionDescription}>
                <RichText html={stepInfo.description ?? ""}></RichText>
                {props.step === Step.BUDGET && (
                    <div className={styles.helpfulTip}>
                        <Link
                            href={financeLink || homeLink}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <span
                                dangerouslySetInnerHTML={{
                                    __html:
                                        strings.get(
                                            "MATTRESS_SELECTOR_BUDGET_TIP",
                                        ) || "",
                                }}
                            />
                        </Link>
                    </div>
                )}
            </section>
        );
    };

    const options = stepInfo.data;

    function buildOptions(option: StepDataType) {
        const optionClasses = classNames({
            [styles.listItem]: true,
            [styles.checked]: selectedLevel === option.level,
        });
        const icon = icons[option.icon as keyof typeof icons];
        return (
            <li key={option.label} className={optionClasses}>
                <FormCheckbox
                    className="radio"
                    checked={selectedLevel === option.level}
                    id={option.label}
                    name={option.label}
                    value={option.level.toString()}
                    onChange={() => {
                        onClick(option);
                    }}
                    required={true}
                    readOnly
                />
                <label htmlFor={option.label} aria-selected={false}>
                    {icon && (
                        <SVG
                            aria-hidden="true"
                            src={icon}
                            title={option.label}
                            className={props.step}
                            loader={<SVGLoader />}
                        />
                    )}
                    <div>{option.label}</div>
                </label>
            </li>
        );
    }

    const classes = classNames({
        [styles.list]: true,
        [props.step.toLowerCase()]: true,
    });

    const descriptionContainerClasses = classNames({
        [styles.descriptionContainer]: true,
        "description-container": true,
    });

    return (
        <>
            <div className={descriptionContainerClasses}>
                <div className={styles.fieldset}>
                    <h1>
                        <span
                            dangerouslySetInnerHTML={{
                                __html: stepInfo.heading || "",
                            }}
                        ></span>
                    </h1>
                    <ul className={classes}>{options.map(buildOptions)}</ul>
                </div>
                {isLargeScreen && renderBudgetTip()}
            </div>
            {!isLargeScreen && renderBudgetTip()}
        </>
    );
};
